<template>
  <Breadcrumb
    :path="breadcrumb.path"
    icon="ecc:ecc-B-Dashboard"
    :searchTitle="breadcrumb.searchTitle"
    :currentTab="tabs[activeTab].key"
    :searchKey="tabs[activeTab].searchKey"
    :searchModel="getSearchModel"
    :sortModel="getCurrentSortBy"
    :sortQueryKeys="breadcrumb.sortQueryKeys"
    :sortingKeys="breadcrumb.sortingKeys"
    :filterEnabledKeys="breadcrumb.filterEnabledKeys"
    :tagEnabled="true"
    :activeTab="activeTab"
  >
  </Breadcrumb>
  <v-container
    fluid
    class="container maincontainer"
    style="height: calc(100% - 80px)"
  >
    <CreateAlarm @alarmCreated="fetchAlarms"/>
    <EditAlarm @alarmUpdated="fetchAlarms"/>
    <AdvancedAlarm @customAlarmCreated="fetchAlarms" />
    <AlarmCounters></AlarmCounters>
    <StateMachine></StateMachine>
    <HelpDrawer id="PageBottomHelp">
      <div v-html="bottomHelp"></div>
    </HelpDrawer>
    <v-tabs class="main-tabs" v-model="activeTab" align-tabs="title">
      <v-tab style="text-transform: capitalize" v-for="tab in tabs">
        {{ tab.key }}
      </v-tab>

      <v-layout class="additionalHeader align-center">
        <template v-if="activeTab === 0">
          <v-spacer></v-spacer>
          <AnimatedButton icon="ecc:ecc-T-Add" @click="dataStore.openCreate" />
          <AnimatedButton style="margin-left:10px" icon="ecc:ecc-T-Help" @click="showHelp"></AnimatedButton>
        </template>
      </v-layout>
    </v-tabs>

    <v-window v-model="activeTab" class="fill-height">
      <v-window-item class="fill-height">
        <AlarmTable ref="AlarmTable" v-model="alarms" />
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Breadcrumb from "../components/Breadcrumb.vue";
import CreateAlarm from "../components/CreateAlarm.vue";
import EditAlarm from "../components/EditAlarm.vue";
import AdvancedAlarm from "../components/AdvancedAlarm/AdvancedAlarm.vue";
import StateMachine from "../components/AdvancedAlarm/StateMachine.vue";
import AnimatedButton from "../components/UIComponents/AnimatedButton.vue";
import Help from "../components/Help/Help.vue";
import HelpDrawer from "../components/Help/HelpDrawer.vue";
import { dataStore } from "../stores/data";
import { ALARM_TYPES_DB, AlarmOrderBy, SortDTO } from "../utils/type.util";
import { useI18n } from "vue-i18n";
import AlarmTable from "../components/AlarmTable.vue";
import { IAlarmFilter } from "../interfaces/IAlarm";
import { createAlarmsState } from "../components/AlarmsState";
import { store as HelpStore } from "../components/Help/Help.store";
import AlarmsHelp from "../components/Help/AlarmsHelp";
import PoliciesHelp from "../components/Help/PoliciesHelp";
import ActionsHelp from "../components/Help/ActionsHelp";
import AlarmCounters from "../components/AlarmCounters.vue";

const TAB_KEYS = {
  ALARMS: "alarms",
};

enum TAB_KEYS_INDEX {
  ALARMS = 0,
}

export default defineComponent({
  setup() {
    const { t } = useI18n();
    return { t };
  },
  name: "Page",
  components: {
    Breadcrumb,
    AnimatedButton,
    CreateAlarm,
    EditAlarm,
    Help,
    HelpDrawer,
    AdvancedAlarm,
    StateMachine,
    AlarmTable,
    AlarmCounters,
  },
  data() {
    return {
      helpStore: HelpStore,
      tabs: [
        {
          key: TAB_KEYS.ALARMS,
          label: TAB_KEYS.ALARMS,
          searchKey: "searchAlarms",
        },
      ] as {
        key: string;
        label: string;
        searchKey: string;
      }[],
      breadcrumb: {
        path: [this.t('alarmManager')],
        icon: "ecc:ecc-B-Dashboard",
        searchTitle: "Search...",
        sortQueryKeys: {
          [TAB_KEYS.ALARMS]: "sortBy_alarms",
        },
        sortingKeys: {
          [TAB_KEYS.ALARMS]: {
            name: "Sort by name",
            updatedAt: "Sort by update time",
          },
        },
        filterEnabledKeys: {
          [TAB_KEYS.ALARMS]: "filterEnabled_alarms",
        },
      },
      ALARM_TYPES_DB: ALARM_TYPES_DB,
      activeTab: 0,
      dataStore: dataStore(),
      alarms: createAlarmsState(),
    };
  },
  methods: {
    showHelp() {
      this.helpStore.drawerItems["PageBottomHelp"]?.toggle();
    },
    async fetchAlarms() {
      const Component = this.$refs["AlarmTable"];
      if (Component && Component.refresh) {
        Component.refresh();
      }
    },
  },
  watch: {
    async "$route.query"(val) {
      if (this.activeTab == 0) {
        const filters: IAlarmFilter = {
          orderBy: val.sortBy_alarms?.startsWith("updatedAt")
            ? AlarmOrderBy.updatedAt
            : AlarmOrderBy.name,
          orderDir:
            val.sortBy_alarms && val.sortBy_alarms.endsWith("Desc")
              ? SortDTO.DESC
              : SortDTO.ASC,
        };
        if (val.filterEnabled_alarms == 'true') {
          if (val.alarmsname) filters.name = val.alarmsname;
          if (val.alarmstype) filters.type = val.alarmstype;
        } else {
          if (val.searchAlarms) filters.search = val.searchAlarms;
        }
        
        this.alarms.filters = filters;
        await this.fetchAlarms();
      }
    },
  },
  computed: {
    bottomHelp(): string {
      switch (this.activeTab) {
        case TAB_KEYS_INDEX.ALARMS:
          return AlarmsHelp.getLocaleBottomHelp();
        default:
          return "";
      }
    },
    getAlarms(): Object {
      const fetchedAlarms = this.dataStore.getAlarms;
      return {
        ...fetchedAlarms,
        content: fetchedAlarms.content?.map((d) => {
          return { ...d, selected: false };
        }),
      };
    },
    getCurrentSortBy(): any {
      return Object.fromEntries(
        Object.values(TAB_KEYS).map((sv) => [
          sv,
          this.$route.query[this.breadcrumb.sortQueryKeys[sv]],
        ])
      );
    },
    getSearchModel(): string {
      return this.$route.query[this.tabs[this.activeTab].searchKey];
    },
  },
  mounted() {
    // Register help drawer to help store
    this.helpStore.add("PageBottomHelp");
    this.helpStore.add("HelpSideDrawer");
    this.helpStore.add("HelpEditSideDrawer");


    // Load help data asyncronously
    AlarmsHelp.fetchHelpData();
    PoliciesHelp.fetchHelpData();
    ActionsHelp.fetchHelpData();
  }
});
</script>

<style scoped></style>
